import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FiClock,
  FiCalendar,
  FiBriefcase,
  FiFileText,
  FiMessageSquare,
  FiCheckCircle,
  FiXCircle,
  FiVideo,
  FiAlertCircle,
  FiRefreshCw
} from "react-icons/fi";
import {
  MdOutlinePendingActions,
  MdOutlineCheckCircleOutline,
  MdOutlineCancel,
  MdOutlineAccessTime,
  MdOutlineEventAvailable
} from "react-icons/md";
import { MdOutlineFormatListNumbered } from "react-icons/md";
import "../styles/UserAppointmentDetails.css";

export default function UserAppointments({ defaultExpandedId }) {
  const { token } = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [mainTab, setMainTab] = useState("PENDING");
  const [completedTab, setCompletedTab] = useState("ACCEPTED");

  const [stats, setStats] = useState({
    pending: 0,
    accepted: 0,
    rejected: 0
  });

  /* ======================
     FETCH APPOINTMENTS
  ====================== */
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/appointments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to fetch appointments");

      const data = await res.json();
      setAppointments(data.appointments || []);

      // Calculate stats
      const pendingCount = data.appointments?.filter(app =>
        app.status === "PENDING" ||
        (app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED")
      ).length || 0;

      const acceptedCount = data.appointments?.filter(app =>
        app.status === "ACCEPTED" && app.appointmentCallStatus === "COMPLETED"
      ).length || 0;

      const rejectedCount = data.appointments?.filter(app =>
        app.status === "REJECTED"
      ).length || 0;

      setStats({
        pending: pendingCount,
        accepted: acceptedCount,
        rejected: rejectedCount
      });

    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ======================
 TOGGLE DETAILS (FINAL FIX)
====================== */
  const toggleDetails = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };


  /* ======================
     JOIN VIDEO CALL
  ====================== */
  const handleJoinVideoCall = (appointment) => {
    Swal.fire({
      title: "Join Video Call",
      html: `
                <div style="text-align: left; line-height: 1.6;">
                    <p><strong>Solicitor:</strong> ${appointment.solicitorId?.name || "Not specified"}</p>
                    <p><strong>Date:</strong> ${new Date(appointment.appointmentDate).toLocaleString()}</p>
                    <p><strong>Practice Area:</strong> ${appointment.practiceArea || "Not specified"}</p>
                    
                    <div style="margin-top: 15px; background: #f0f9ff; padding: 12px; border-radius: 8px; border-left: 4px solid #3b82f6;">
                        <p style="margin: 0; color: #1e40af; font-weight: 500;">
                            <strong>Important:</strong> Ensure you have a stable internet connection and required permissions for video calls.
                        </p>
                    </div>
                </div>
            `,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Join Call Now",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6b7280",
      width: "550px"
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(appointment.videoCallLink, "_blank");
      }
    });
  };

  /* ======================
     FILTER APPOINTMENTS
  ====================== */
  const pendingAppointments = appointments.filter(
    (app) =>
      app.status === "PENDING" ||
      (app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED")
  );

  const completedAccepted = appointments.filter(
    (app) =>
      app.status === "ACCEPTED" &&
      app.appointmentCallStatus === "COMPLETED"
  );

  const completedRejected = appointments.filter(
    (app) => app.status === "REJECTED"
  );

  let visibleAppointments = [];

  if (mainTab === "PENDING") {
    visibleAppointments = pendingAppointments;
  } else {
    visibleAppointments =
      completedTab === "ACCEPTED"
        ? completedAccepted
        : completedRejected;
  }

  /* ======================
     RENDER STATUS BADGE
  ====================== */
  const renderStatusBadge = (status, callStatus) => {
    let badgeConfig = {
      icon: <FiAlertCircle size={14} />,
      label: "Unknown",
      color: "#6b7280",
      bgColor: "#f3f4f6"
    };

    if (status === "PENDING") {
      badgeConfig = {
        icon: <MdOutlinePendingActions size={14} />,
        label: "Pending Review",
        color: "#f59e0b",
        bgColor: "#fef3c7"
      };
    } else if (status === "ACCEPTED") {
      if (callStatus === "NOT_STARTED") {
        badgeConfig = {
          icon: <MdOutlineAccessTime size={14} />,
          label: "Scheduled",
          color: "#3b82f6",
          bgColor: "#dbeafe"
        };
      } else if (callStatus === "COMPLETED") {
        badgeConfig = {
          icon: <FiCheckCircle size={14} />,
          label: "Completed",
          color: "#10b981",
          bgColor: "#d1fae5"
        };
      } else {
        badgeConfig = {
          icon: <FiCheckCircle size={14} />,
          label: "Accepted",
          color: "#10b981",
          bgColor: "#d1fae5"
        };
      }
    } else if (status === "REJECTED") {
      badgeConfig = {
        icon: <FiXCircle size={14} />,
        label: "Rejected",
        color: "#dc2626",
        bgColor: "#fee2e2"
      };
    }

    return (
      <span
        className="user-appointments-status-badge"
        style={{
          backgroundColor: badgeConfig.bgColor,
          color: badgeConfig.color
        }}
      >
        {badgeConfig.icon}
        <span>{badgeConfig.label}</span>
      </span>
    );
  };

  const getEndTime = (startDate, durationMinutes) => {
    const end = new Date(startDate);
    end.setMinutes(end.getMinutes() + (durationMinutes || 0));
    return end;
  };

  /* ======================
     RENDER STATS CARDS
  ====================== */
  const renderStatsCards = () => (
    <div className="user-appointments-stats-grid">
      <div className="user-appointments-stat-card user-appointments-stat-pending">
        <div className="user-appointments-stat-icon">
          <MdOutlinePendingActions size={24} />
        </div>
        <div className="user-appointments-stat-content">
          <h3 className="user-appointments-stat-value">{stats.pending}</h3>
          <p className="user-appointments-stat-label">Pending</p>
          <span className="user-appointments-stat-subtext">Awaiting Action</span>
        </div>
      </div>

      <div className="user-appointments-stat-card user-appointments-stat-accepted">
        <div className="user-appointments-stat-icon">
          <MdOutlineCheckCircleOutline size={24} />
        </div>
        <div className="user-appointments-stat-content">
          <h3 className="user-appointments-stat-value">{stats.accepted}</h3>
          <p className="user-appointments-stat-label">Accepted</p>
          <span className="user-appointments-stat-subtext">Completed Meetings</span>
        </div>
      </div>

      <div className="user-appointments-stat-card user-appointments-stat-rejected">
        <div className="user-appointments-stat-icon">
          <MdOutlineCancel size={24} />
        </div>
        <div className="user-appointments-stat-content">
          <h3 className="user-appointments-stat-value">{stats.rejected}</h3>
          <p className="user-appointments-stat-label">Rejected</p>
          <span className="user-appointments-stat-subtext">Cancelled Appointments</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="premium-user-appointments">
      {/* ======================
               HEADER
            ====================== */}
      <div className="user-appointments-header">
        <div className="user-appointments-header-left">
          <h1 className="user-appointments-page-title">My Appointments</h1>
          <p className="user-appointments-page-subtitle">
            Manage and track your legal consultation appointments
          </p>
        </div>
        <div className="user-appointments-header-right">
          <button
            className="user-appointments-refresh-btn"
            onClick={fetchAppointments}
            disabled={loading}
          >
            <FiRefreshCw size={18} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* ======================
               STATS
            ====================== */}
      {renderStatsCards()}

      {/* ======================
               MAIN CONTENT
            ====================== */}
      <div className="user-appointments-content">
        {/* Tabs */}
        <div className="user-appointments-tabs-container">
          {/* Main Tabs */}
          <div className="user-appointments-main-tabs">
            {["PENDING", "COMPLETED"].map(tab => (
              <button
                key={tab}
                className={`user-appointments-tab-btn ${mainTab === tab ? "user-appointments-tab-active" : ""}`}
                onClick={() => {
                  setMainTab(tab);
                  setExpandedId(null);
                  if (tab === "COMPLETED") {
                    setCompletedTab("ACCEPTED");
                  }
                }}
              >
                {tab === "PENDING" && <MdOutlinePendingActions size={16} />}
                {tab === "COMPLETED" && <MdOutlineCheckCircleOutline size={16} />}
                <span>
                  {tab === "PENDING" && `Pending (${stats.pending})`}
                  {tab === "COMPLETED" && `Completed (${stats.accepted + stats.rejected})`}
                </span>
              </button>
            ))}
          </div>

          {/* Sub Tabs for Completed */}
          {mainTab === "COMPLETED" && (
            <div className="user-appointments-sub-tabs">
              {["ACCEPTED", "REJECTED"].map(subTab => (
                <button
                  key={subTab}
                  className={`user-appointments-sub-tab-btn ${completedTab === subTab ? "user-appointments-sub-tab-active" : ""}`}
                  onClick={() => setCompletedTab(subTab)}
                >
                  {subTab === "ACCEPTED" && <FiCheckCircle size={14} />}
                  {subTab === "REJECTED" && <FiXCircle size={14} />}
                  <span>
                    {subTab === "ACCEPTED" && `Accepted (${stats.accepted})`}
                    {subTab === "REJECTED" && `Rejected (${stats.rejected})`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ======================
                   APPOINTMENTS LIST
                ====================== */}
        <div className="user-appointments-list-container">
          {loading ? (
            <div className="user-appointments-loading-state">
              <div className="user-appointments-loading-spinner"></div>
              <p>Loading appointments...</p>
            </div>
          ) : visibleAppointments.length === 0 ? (
            <div className="user-appointments-empty-state">
              <MdOutlineEventAvailable size={48} />
              <p>No appointments found</p>
              <small>Try selecting a different tab or check back later</small>
            </div>
          ) : (
            <div className="user-appointments-cards-grid">
              {visibleAppointments.map((app) => (
                <div key={app._id} className="user-appointments-card">
                  {/* Card Header */}
                  <div className="user-appointments-card-header">
                    <div className="user-appointments-solicitor-info">
                      <div className="user-appointments-solicitor-avatar">
                        {app.solicitorId?.profile?.photo ? (
                          <img
                            src={app.solicitorId.profile.photo}
                            alt={app.solicitorId.name}
                            className="solicitor-avatar-img"
                            onError={(e) => {
                              console.log("Image failed to load:", e.target.src);
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="solicitor-avatar-fallback">
                            {app.solicitorId?.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="user-appointments-solicitor-details">
                        <div className="user-appointments-solicitor-name">
                          {app.solicitorId?.name || "Solicitor Not Specified"}
                        </div>
                        <div className="user-appointments-solicitor-meta">
                          <FiBriefcase size={12} />
                          <span>{app.practiceArea || "Practice Area"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="user-appointments-status-container">
                      {renderStatusBadge(app.status, app.appointmentCallStatus)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="user-appointments-card-body">
                    <div className="user-appointments-datetime-info">
                      <div className="user-appointments-date-info">
                        <FiCalendar size={14} />
                        <span>
                          {new Date(app.appointmentDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="user-appointments-time-info">
                        <FiClock size={14} />
                        <span>
                          {new Date(app.appointmentDate).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                          {" - "}
                          {getEndTime(
                            app.appointmentDate,
                            app.appointmentDurationMinutes
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>
                      <p className="solicitor-appointment-id">
                        <strong>{app.appointmentId}</strong>
                      </p>

                    </div>

                    <div className="user-appointments-case-info">
                      <div className="user-appointments-case-category">
                        <FiFileText size={14} />
                        <span>{app.practiceCategory || "General"}</span>
                      </div>
                      <div className="user-appointments-case-type">
                        <FiMessageSquare size={14} />
                        <span>{app.caseType || "Consultation"}</span>
                      </div>
                      <div className="user-appointments-case-type">
                        <MdOutlineFormatListNumbered size={14} />
                        <span>Slot : {app.slotNumber || "Not Assigned"}</span>
                      </div>
                    </div>

                    <div className="user-appointments-actions">
                      <button
                        className="user-appointments-view-details-btn"
                        onClick={() => toggleDetails(app._id)}
                      >
                        {expandedId === app._id ? "Hide Details" : "View Details"}
                      </button>


                      {app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED" && (
                        <button
                          className="user-appointments-join-call-btn"
                          onClick={() => handleJoinVideoCall(app)}
                        >
                          <FiVideo size={16} />
                          <span>Join Video Call</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === app._id && (
                    <div className="user-appointments-expanded-details">
                      <div className="user-appointments-details-section">
                        <h4>Case Details</h4>
                        <div className="user-appointments-details-grid">
                          <div className="user-appointments-detail-item">
                            <strong>Practice Category:</strong>
                            <span>{app.practiceCategory || "Not specified"}</span>
                          </div>
                          <div className="user-appointments-detail-item">
                            <strong>Practice Area:</strong>
                            <span>{app.practiceArea || "Not specified"}</span>
                          </div>
                          <div className="user-appointments-detail-item">
                            <strong>Case Type:</strong>
                            <span>{app.caseType || "Not specified"}</span>
                          </div>
                          <div className="user-appointments-detail-item">
                            <strong>Booked On:</strong>
                            <span>{new Date(app.createdAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="user-appointments-description-section">
                        <h4>Description</h4>
                        <div className="user-appointments-description-content">
                          {app.caseDescription || "No description provided."}
                        </div>
                      </div>

                      {app.status === "ACCEPTED" && app.appointmentCallStatus === "COMPLETED" && (
                        <div className="user-appointments-completed-section">
                          <div className="user-appointments-completed-badge">
                            <FiCheckCircle size={16} />
                            <span>Meeting Completed Successfully</span>
                          </div>
                        </div>
                      )}

                      {app.status === "REJECTED" && app.rejectionReason && (
                        <div className="user-appointments-rejected-section">
                          <h4>Rejection Reason</h4>
                          <div className="user-appointments-rejection-content">
                            {app.rejectionReason}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}